import React from 'react';
import TimelineLayer from './TimelineLayer';
import '../utils/dragDropTypes';
import 'lucide-react';

const Timeline = ({
    layers,
    currentTime,
    duration,
    zoom,
    onSeek,
    onToggleVisibility,
    onToggleLock,
    onToggleMute,
    onRemoveLayer,
    onAddClip,
    onRemoveClip,
    onUpdateClip,
    onAddTextClip,
    onToggleSharedTransform,
    onReorderLayers,
    onSelectClip,
    onSplitClip,
    onUpdateVolume,
    disabled = false,
}) => {
    const timelineRef = React.useRef(null);
    const containerRef = React.useRef(null);
    const playheadRef = React.useRef(null);
    const [isPlayheadHovered, setIsPlayheadHovered] = React.useState(false);
    const [isPlayheadDragging, setIsPlayheadDragging] = React.useState(false);

    const pixelsPerSecond = 50 * zoom;
    const calculatedWidth = duration * pixelsPerSecond;

    // Ensure timeline is at least as wide as the container
    const [containerWidth, setContainerWidth] = React.useState(0);

    React.useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const timelineWidth = Math.max(calculatedWidth, containerWidth);

    // Total seconds for ruler (rounded up)
    const totalSeconds = Math.ceil(duration);

    const handleTimelineClick = (e) => {
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - 44;
        const time = Math.max(0, x / pixelsPerSecond);
        onSeek(time);
    };

    const handlePlayheadPointerDown = (e) => {
        e.preventDefault();
        setIsPlayheadDragging(true);
        const pointerId = e.pointerId;
        const handlePointerMove = (moveEvent) => {
            if (moveEvent.pointerId !== pointerId) return;
            const rect = timelineRef.current.getBoundingClientRect();
            const x = moveEvent.clientX - rect.left - 44;
            const time = Math.max(0, Math.min(x / pixelsPerSecond, duration));
            onSeek(time);
        };
        const handlePointerUp = (upEvent) => {
            if (upEvent.pointerId !== pointerId) return;
            setIsPlayheadDragging(false);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerUp);
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerUp);
    };

    // Determine label interval based on zoom
    const labelInterval = zoom < 0.3 ? 10 : zoom < 0.5 ? 5 : zoom < 1 ? 2 : 1;

    return (
        <div className="h-full flex flex-col bg-white dark:bg-[#111111] border-[#E5E7EB] dark:border-white/10 relative">
            {/* Disabled Overlay */}
            {disabled && (
                <div className="absolute inset-0 bg-white/50 dark:bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm cursor-wait">
                    <span className="text-gray-900 dark:text-white font-medium bg-white/80 dark:bg-black/80 px-4 py-2 rounded-lg shadow-sm">
                        Loading...
                    </span>
                </div>
            )}

            {/* Timeline Content */}
            <div className="flex-1 flex flex-col relative w-full h-full overflow-hidden" ref={containerRef}>
                {/* Ruler Component (Sticky/Fixed Top) - Must scroll Horizontally in sync with layers */}
                {/* Therefore, wrap both Ruler and Layers in a shared horizontal scroll container */}
                <div className="flex-1 min-h-0 relative w-full h-full overflow-auto">
                    <div className="min-w-max flex flex-col min-h-full relative border-gray-100 dark:border-white/10" style={{ width: `${timelineWidth}px` }}>

                        {/* ── PLAYHEAD: VERTICAL LINE ──────────────────────────────────────
                             z-[499]: intentionally BELOW the ruler (z-[500]).
                             The ruler has a solid opaque background, so when the layers are
                             scrolled upward and the line approaches the ruler zone, the ruler
                             simply paints over the top of the line — perfect natural clipping.
                             The line starts at top-[56px] (bottom of ruler) and ends at bottom. */}
                        <div
                            className="absolute top-0 bottom-0 pointer-events-none z-[499]"
                            style={{
                                left: '44px',
                                width: '40px',
                                transform: `translateX(${currentTime * pixelsPerSecond - 20}px)`,
                                willChange: 'transform',
                            }}
                        >
                            <div className="absolute left-1/2 -translate-x-1/2 top-[56px] bottom-0 w-[2px] bg-orange-500 pointer-events-none" />
                        </div>

                        {/* ── PLAYHEAD: THUMB HANDLE ───────────────────────────────────────
                             z-[1001]: always on top of everything — ruler, line, layers.
                             sticky top-0 keeps the circle visible at the top regardless of
                             how far the layers are scrolled vertically.
                             Same horizontal transform as the line div above. */}
                        <div
                            ref={playheadRef}
                            className="absolute top-0 bottom-0 pointer-events-none z-[1001]"
                            style={{
                                left: '44px',
                                width: '40px',
                                transform: `translateX(${currentTime * pixelsPerSecond - 20}px)`,
                                willChange: 'transform',
                            }}
                        >
                            <div className="sticky top-0 h-[56px] w-full pointer-events-none">
                                {/* Orange circle thumb */}
                                <div
                                    className="video-editor-playhead-handle absolute left-1/2 -translate-x-1/2 top-[4px] w-[14px] h-[14px] rounded-full bg-[#e85d2c] shadow-sm pointer-events-auto cursor-grab"
                                    onMouseEnter={() => setIsPlayheadHovered(true)}
                                    onMouseLeave={() => setIsPlayheadHovered(false)}
                                    onPointerDown={handlePlayheadPointerDown}
                                    style={{ touchAction: 'none' }}
                                />

                                {/* Scrubber arrows stay anchored over the thumb. */}
                                {(isPlayheadHovered || isPlayheadDragging) && (
                                    <div
                                        className="absolute left-1/2 top-[1px] flex items-center gap-0.5 px-2.5 py-1.5 bg-gray-900 rounded-full border-2 border-white shadow-lg pointer-events-none"
                                        style={{ transform: "translateX(-50%)" }}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 16 16" className="text-white">
                                            <path d="M10 13L5 8L10 3V13Z" fill="currentColor" stroke="white" strokeWidth="0.5" />
                                        </svg>
                                        <svg width="12" height="12" viewBox="0 0 16 16" className="text-white">
                                            <path d="M6 3L11 8L6 13V3Z" fill="currentColor" stroke="white" strokeWidth="0.5" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Interactive Invisible Overlay for Playhead Dragging (Spans whole width) */}
                        <div
                            className="absolute inset-0 z-[999] pointer-events-auto opacity-0"
                            onPointerDownCapture={handlePlayheadPointerDown}
                            style={{ display: isPlayheadHovered || isPlayheadDragging ? 'block' : 'none', touchAction: 'none' }}
                        />

                        {/* Time Ruler (Fixed Height, Sticky Top) */}
                        <div className="sticky top-0 z-[500] flex bg-white dark:bg-[#111111] pt-[16px]">
                            <div
                                ref={timelineRef}
                                className="flex-1 h-10 bg-white dark:bg-[#111111] border-b border-[#E5E7EB] dark:border-white/10 relative cursor-pointer"
                                onClick={handleTimelineClick}
                            >
                                <div className="absolute inset-0 pl-11 pointer-events-none">
                                    <div className="relative h-full">
                                        {/* Tick marks */}
                                        <div className="absolute top-2 left-0 right-0 h-3">
                                            {/* Major ticks (dark lines at each second) */}
                                            {Array.from({ length: totalSeconds + 1 }).map((_, i) => (
                                                <div
                                                    key={`major-${i}`}
                                                    className="absolute w-px h-3 bg-gray-500"
                                                    style={{
                                                        left: `${i * pixelsPerSecond}px`,
                                                        transform: 'translateX(-50%)',
                                                    }}
                                                />
                                            ))}

                                            {/* Minor ticks (4 lighter lines between each second) */}
                                            {Array.from({ length: totalSeconds }).map((_, secondIndex) => (
                                                [1, 2, 3, 4].map((tickNum) => (
                                                    <div
                                                        key={`minor-${secondIndex}-${tickNum}`}
                                                        className="absolute w-px h-3 bg-gray-300 dark:bg-gray-700"
                                                        style={{
                                                            left: `${(secondIndex + tickNum / 5) * pixelsPerSecond}px`,
                                                            transform: 'translateX(-50%)',
                                                        }}
                                                    />
                                                ))
                                            ))}
                                        </div>

                                        {/* Labels below ticks */}
                                        <div className="absolute top-4 left-0 right-0">
                                            {Array.from({ length: totalSeconds + 1 }).map((_, i) => {
                                                if (i % labelInterval !== 0) return null;
                                                return (
                                                    <div
                                                        key={`label-${i}`}
                                                        className="absolute"
                                                        style={{
                                                            left: `${i * pixelsPerSecond}px`,
                                                            transform: 'translateX(-50%)',
                                                        }}
                                                    >
                                                        <span className="text-xs whitespace-nowrap text-gray-600 dark:text-gray-400">
                                                            {i}s
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Layers Container (Vertical scroll handled by outer container) */}
                        <div
                            className="flex-1 pl-3 pb-10"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) onSelectClip(null);
                            }}
                        >
                            {layers.map((layer, index) => (
                                <TimelineLayer
                                    key={layer.id}
                                    index={index}
                                    layer={layer}
                                    currentTime={currentTime}
                                    onToggleVisibility={onToggleVisibility}
                                    onToggleLock={onToggleLock}
                                    onToggleMute={onToggleMute}
                                    onUpdateClip={onUpdateClip}
                                    onAddTextClip={onAddTextClip}
                                    onToggleSharedTransform={onToggleSharedTransform}
                                    pixelsPerSecond={pixelsPerSecond}
                                    onSelectClip={onSelectClip}
                                    onSeek={onSeek}
                                    onRemove={onRemoveLayer}
                                    onAddClip={onAddClip}
                                    onRemoveClip={onRemoveClip}
                                    onSplitClip={onSplitClip}
                                    onReorderLayers={onReorderLayers}
                                    onUpdateVolume={onUpdateVolume}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div >
    );
};

export default Timeline;
